import { defineStore } from 'pinia';
import { useAuthStore, useCommunityStore } from '.';
import apiService from '@/services/api.service';
import { Config } from '@/config';

import type { MailingCampaignDto } from '@/services/types/api.types';
import { useI18n } from 'vue-i18n';
import { Constants } from '@/constants';
import router from '@/router';

export interface MailingCampaignState {
  mailingCampaigns: MailingCampaignDto[];
  isLoading: boolean;
  itemLoadingStates: Set<number>;
}

function getStatuses(): any[] {
  const { t } = useI18n();
  return [
    { label: t('campaign.status.options.draft'), value: Constants.Campaign.Status.Draft },
    { label: t('campaign.status.options.sent'), value: Constants.Campaign.Status.Sent },
  ];
}

export const useMailingCampaignStore = defineStore({
  id: 'mailingCampaigns',
  state: (): MailingCampaignState => ({
    mailingCampaigns: [],
    isLoading: false,
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    isItemLoading: (state) => (id: number) => state.itemLoadingStates.has(id),
    getItem:
      (state) =>
      (id: number): MailingCampaignDto | undefined =>
        state.mailingCampaigns.find((c) => c.id === id),
    getStatuses: (state) => getStatuses,
  },
  actions: {
    async fetchMailingCampaign(id: number) {
      this.itemLoadingStates.add(id);
      try {
        const { data } = await apiService.get<MailingCampaignDto>(`${Config.api.v2}/mailing-campaigns/${id}`);
        const index = this.mailingCampaigns.findIndex((c) => c.id === id);
        if (index !== -1) {
          this.mailingCampaigns[index] = data;
        } else {
          this.mailingCampaigns.push(data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(id);
      }
    },
    async fetchMailingCampaigns() {
      this.isLoading = true;
      try {
        const { data } = await apiService.get<MailingCampaignDto[]>(`${Config.api.v2}/mailing-campaigns`);
        this.mailingCampaigns = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async updateMailingCampaigns(...mailingCampaigns: MailingCampaignDto[]) {
      try {
        for (const mailingCampaign of mailingCampaigns) {
          const response = await apiService.put(
            `${Config.api.v2}/mailing-campaigns/${mailingCampaign.id}`,
            mailingCampaign,
          );
          // Handle the response, e.g., updating the state, calling mutations, etc.
        }
      } catch (error) {
        this.error = error.message;
      }
    },
    createMailingCampaign(): MailingCampaignDto {
      const authStore = useAuthStore();
      return {
        community_id: authStore.currentCommunity ?? -1,
        title: { en: '', fr: '' },
        subject: { en: '', fr: '' },
        html_body: { en: '', fr: '' },
        text_body: { en: '', fr: '' },
        status: 1,
      };
    },
    async saveMailingCampaign(mailingCampaign: MailingCampaignDto) {
      try {
        let mailingCampaignId = mailingCampaign.id;
        if (mailingCampaignId) {
          await apiService.put<MailingCampaignDto>(
            `${Config.api.v2}/mailing-campaigns/${mailingCampaign.id}`,
            mailingCampaign,
          );
        } else {
          mailingCampaign.subject = { en: '', fr: '' };
          mailingCampaign.html_body = { en: '', fr: '' };
          mailingCampaign.text_body = { en: '', fr: '' };
          const { data } = await apiService.post<MailingCampaignDto>(
            `${Config.api.v2}/mailing-campaigns`,
            mailingCampaign,
          );
          mailingCampaignId = data.id;
          router.push({ name: 'mailing_campaign_detail', params: { id: mailingCampaignId } });
        }
        await this.fetchMailingCampaign(mailingCampaignId!);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },

    async deleteMailingCampaign(mailingCampaignId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/mailing-campaigns/${mailingCampaignId}`);
        await this.fetchMailingCampaigns();
      } catch (error) {
        console.error(error);
      }
    },
    async sendTestEmail(mailingCampaignId: number, values: any) {
      try {
        const response = await apiService.post(
          `${Config.api.v2}/mailing-campaigns/${mailingCampaignId}/:send-test-email`,
          { email: values.email },
        );
        return true;
      } catch (e) {
        console.error(error);
        return false;
      }
    },
    async sendCampaign(mailingCampaignId: number) {
      try {
        const response = await apiService.post(
          `${Config.api.v2}/mailing-campaigns/${mailingCampaignId}/:send-campaign`,
        );
        await this.fetchMailingCampaign(mailingCampaignId);
        return response;
      } catch (e) {
        console.error(error);
        return false;
      }
    },
  },
});
